// Persona-switch recovery (PRD #156).
//
// Staff persona switching is optimistic from the UI's perspective: the
// shell reflects the selected persona immediately while the server-side
// switch + audit write (ADR-0004) complete in the background. If the
// server denies the switch (the user lost the grant, or the audit write
// fails) the shell must recover to a persona it is allowed to act as,
// rather than sit on a view the server will gate every read against.
//
// `resolvePersonaFallback` is the pure recovery rule, kept separate so the
// success / failure / unauthorised paths are unit-testable without a DOM.

import type { QueryClient } from '@tanstack/react-query';
import { type FrontendKey, type Persona, IM_ASSISTANT_TOKEN } from '@septima/contract';
import { SESSION_QUERY_KEY } from '@/lib/auth';

/**
 * The active persona a given shell should run as for this user (ADR-0004).
 * Identity for the four frontends; but on the Desk shell an `im-assistant`-only
 * user (granted `im-assistant`, not `desk`) runs as `im-assistant` — so the
 * layout never tries to switch them INTO the ungranted `desk` persona.
 */
export function intendedPersonaForFrontend(
    frontend: FrontendKey,
    grantedTokens: readonly string[],
): Persona {
    if (
        frontend === 'desk' &&
        !grantedTokens.includes('desk') &&
        grantedTokens.includes(IM_ASSISTANT_TOKEN)
    ) {
        return IM_ASSISTANT_TOKEN;
    }
    return frontend;
}

/**
 * Cross-persona cache wall (ADR-0004). Persona switching is an SPA
 * navigation — no reload — and every domain hook's query key is
 * persona-blind, so without this a payload fetched under the PREVIOUS
 * persona could render after the switch (the sharp case: a Suitability
 * Record detail cached under `comply` carries the KYC `assessmentContent`
 * block behind the compliance wall — ADR-0015 D5 — and the detail component
 * is shared across views). Drop every cached domain query on a real persona
 * change; the session entry is deliberately KEPT so the shell does not
 * flash back into hydration (a full `queryClient.clear()` would blank
 * `user` mid-switch). Mounted queries refetch under the new persona.
 */
export function clearCrossPersonaCaches(queryClient: QueryClient): void {
    const sessionRoot = SESSION_QUERY_KEY[0];
    queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== sessionRoot,
    });
}

/**
 * The same cache wall for an IM book switch, which does NOT navigate when the
 * reader is already on a Desk page.
 *
 * `removeQueries` drops the cache entry and leaves a MOUNTED observer with
 * nothing: it holds no data and it does not fetch again by itself. A persona
 * switch never showed this, because it routes to another frontend and the
 * remount does the fetching. A book switch on the Desk Dashboard has no
 * remount, so the page emptied and STAYED empty until the next navigation —
 * the reader pressed "switch to Edward's book" and read a blank dashboard.
 *
 * `resetQueries` gives both halves at once: it returns each query to its
 * initial state, so no row of the previous book can render, AND it refetches
 * the queries that are on screen. Call it only once the new book cookie has
 * landed, or the refetch reads the previous book.
 */
export function resetCrossBookCaches(queryClient: QueryClient): void {
    const sessionRoot = SESSION_QUERY_KEY[0];
    void queryClient.resetQueries({
        predicate: (query) => query.queryKey[0] !== sessionRoot,
    });
}

/**
 * The seams one IM book switch needs. The sidebar gives the real ones, and a
 * test gives fakes, so the order below is pinned without a DOM.
 */
export interface BookSwitchPorts {
    /** Move the highlighted switcher button at once (the optimistic paint). */
    paint: (userId: string) => void;
    /** Ask the server for the book. It answers when the session cookie holds it. */
    request: (userId: string) => Promise<{ ok: boolean }>;
    /** The cache the cross-book wall clears. */
    queryClient: QueryClient;
    /** Read the session again. This corrects the paint when the server refuses. */
    refresh: () => Promise<void>;
    /** Tell the reader that the server refused the book. */
    onRefused: () => void;
}

/**
 * Run one IM book switch, from the press to the reconciled session.
 *
 * The book is a fact of the server session (ADR-0004, ADR-0005), and the
 * server audits each change. The shell therefore owns no copy of it: it paints
 * the new button at once, then it waits for the server.
 *
 * The switch was fire-and-forget before, and that lost the book of a reader
 * who moved to another Desk page immediately after the press. The press
 * started the request, nothing held the reader, the navigation cancelled the
 * request, and the next page read the previous book with the new button
 * highlighted. The promise this function returns is the in-flight window. The
 * caller awaits it, disables the switcher for that window, and does its own
 * navigation only after it. `switchActingFor` covers the navigation the
 * caller does not control, because that request is `keepalive`.
 *
 * The order of the last two steps matters. The wall goes up only after the
 * server answers, because a refetch that starts earlier reads the PREVIOUS
 * book. See `resetCrossBookCaches` for why the wall resets and does not
 * remove.
 */
export async function runBookSwitch(userId: string, ports: BookSwitchPorts): Promise<void> {
    ports.paint(userId);
    const result = await ports.request(userId);
    if (!result.ok) ports.onRefused();
    resetCrossBookCaches(ports.queryClient);
    await ports.refresh();
}

/**
 * Choose the persona to fall back to after a denied / ungranted switch to
 * `attempted`. Prefers the previously-active persona when it is still
 * granted and is not the one that just failed; otherwise the first granted
 * persona that is not `attempted`. Returns `null` when there is no other
 * valid persona to recover to (the shell then stays put).
 */
export function resolvePersonaFallback(
    previousPersona: string | null,
    granted: readonly FrontendKey[],
    attempted: FrontendKey,
): FrontendKey | null {
    if (
        previousPersona &&
        previousPersona !== attempted &&
        (granted as readonly string[]).includes(previousPersona)
    ) {
        return previousPersona as FrontendKey;
    }
    return granted.find((g) => g !== attempted) ?? null;
}
